import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import IconAutocomplete from "../../../../components/forms/IconAutocomplete";
import DashboardCustomerOverviewPanel from "./DashboardCustomerOverviewPanel";
import { useDashboardOverviewCustomers } from "../hooks/useDashboardOverviewCustomers";

const DashboardOverviewPanels: React.FC = () => {
  const { t } = useTranslation();
  const { isAdminUser, customers, loading, error } = useDashboardOverviewCustomers();
  const [activeTab, setActiveTab] = React.useState(0);
  const customerOptions = useMemo(
    () =>
      customers
        .filter((customer) => Boolean(customer.id?.trim()))
        .map((customer) => ({
          value: customer.id!.trim(),
          label: customer.name?.trim() || customer.id!.trim(),
          icon: "business",
        })),
    [customers],
  );

  React.useEffect(() => {
    if (activeTab >= customers.length) {
      setActiveTab(0);
    }
  }, [activeTab, customers.length]);

  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{ border: 1, borderColor: "divider", borderRadius: 1, mb: 3, py: 5 }}
      >
        <Stack alignItems="center" spacing={1}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            Loading customer overview…
          </Typography>
        </Stack>
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Failed to load customers for dashboard overview: {error}
      </Alert>
    );
  }

  if (customers.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        {isAdminUser
          ? "No customers found. Overview panels require at least one customer."
          : "No customer profile is linked to your account. Overview panels require an assigned customer."}
      </Alert>
    );
  }

  const panelShellSx = {
    border: 1,
    borderColor: "divider",
    borderRadius: 1,
    overflow: "hidden",
    bgcolor: "background.paper",
    mb: 3,
  } as const;

  if (!isAdminUser) {
    const linkedCustomer = customers[0]!;
    return (
      <Paper elevation={0} sx={panelShellSx}>
        <OverviewHeader
          title={t("dashboard.customerOverview")}
          customerName={linkedCustomer.name?.trim() || linkedCustomer.id}
        />
        <DashboardCustomerOverviewPanel customer={linkedCustomer} embedded />
      </Paper>
    );
  }

  const activeCustomer = customers[activeTab] ?? customers[0]!;

  return (
    <Paper elevation={0} sx={panelShellSx}>
      <OverviewHeader
        title={t("dashboard.customerOverview")}
        customerName={activeCustomer.name?.trim() || activeCustomer.id}
        customerFilter={
          <IconAutocomplete
            options={customerOptions}
            value={activeCustomer.id?.trim() || null}
            onChange={(next) => {
              const index = customers.findIndex((customer) => customer.id?.trim() === next);
              setActiveTab(index >= 0 ? index : 0);
            }}
            placeholder={t("dashboard.selectCustomer")}
            disableClearable
            fullWidth={false}
            size="medium"
            sx={customerHeaderSelectSx}
          />
        }
      />
      <DashboardCustomerOverviewPanel
        key={activeCustomer.id}
        customer={activeCustomer}
        embedded
      />
    </Paper>
  );
};

const customerHeaderSelectSx = {
  minWidth: { xs: 0, sm: 220 },
  maxWidth: { xs: "100%", sm: 360 },
  width: { xs: "100%", sm: "min(360px, 100%)" },
  "& .MuiOutlinedInput-root": {
    fontWeight: 800,
    fontSize: { xs: "1.05rem", sm: "1.2rem" },
    minHeight: { xs: 48, sm: 40 },
    bgcolor: "transparent",
  },
  "& .MuiInputBase-input": {
    fontWeight: 800,
    fontSize: { xs: "1.05rem", sm: "1.2rem" },
    py: { xs: 1.25, sm: 0.75 },
  },
} as const;

function OverviewHeader({
  title,
  customerName,
  customerFilter,
}: {
  title: string;
  customerName?: string;
  customerFilter?: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        px: 2,
        py: { xs: 1.5, sm: 1 },
        borderBottom: 1,
        borderColor: "divider",
        bgcolor: (theme) =>
          theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "grey.50",
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
        justifyContent: "space-between",
        gap: { xs: 1, sm: 2 },
        flexWrap: "wrap",
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1, order: { xs: 1, sm: 0 } }}>
        {customerFilter ?? (
          customerName ? (
            <Typography
              component="span"
              sx={{ fontWeight: 800, fontSize: "1.2rem", lineHeight: 1.3 }}
              noWrap
              title={customerName}
            >
              {customerName}
            </Typography>
          ) : null
        )}
      </Box>
      <Typography
        variant="subtitle2"
        fontWeight={700}
        color="text.secondary"
        sx={{ flexShrink: 0, order: { xs: 0, sm: 1 } }}
      >
        {title}
      </Typography>
    </Box>
  );
}

export default DashboardOverviewPanels;
